import { create } from 'zustand';
import useGameStore, { CLOUD_SNAPSHOT_VERSION } from './gameStore';
import type { CloudGameSnapshot } from './gameStore';
import { buildCloudPayload, safeSlotId } from '../lib/cloudSave';
import { hasPersistedSession, isConfigPresent } from '../lib/firebaseConfig';
import type { CloudUser } from '../lib/firebaseConfig';
import { t } from '../i18n';

/**
 * Google sign-in + Firestore cloud saves.
 *
 * The Firebase SDK is loaded on demand (`await import`): on boot only for players with a
 * persisted session, otherwise the first time they click "Sign in with Google". Anonymous
 * visitors therefore never download the SDK. Autosave debounces the complete game snapshot
 * on every round / turn boundary and writes it to `users/{uid}/games/activeGame`; named slots
 * and archived finished matches live in the same `users/{uid}/games` collection.
 */

export type CloudStatus = 'OFF' | 'IDLE' | 'LOADING' | 'SAVING' | 'SAVED' | 'ERROR';

/** Debounce window for autosaves — one round transition produces exactly one write. */
export const CLOUD_AUTOSAVE_DEBOUNCE_MS = 1200;

/** One named entry listed in the Save Manager (backed by `users/{uid}/games/{saveId}`). */
export interface CloudSaveSlotSummary {
  id: string;
  name: string;
  updatedAt: number;
  summary: { round: number; playerCount: number; leaderName: string; activePlayerName: string; userCash: number };
}

export interface CloudState {
  /** Firebase config present → the sign-in button is offered. */
  configured: boolean;
  initialized: boolean;
  /** Firebase SDK actually running in this session. */
  wired: boolean;
  user: CloudUser | null;
  status: CloudStatus;
  error: string | null;
  signingIn: boolean;
  lastSavedRound: number | null;
  lastSavedAt: number | null;
  /** Cloud snapshot waiting for the Resume / New Game decision. */
  pending: CloudGameSnapshot | null;
  promptOpen: boolean;
  /** Named manual save slots (Save Manager). */
  saveSlots: CloudSaveSlotSummary[];
  saveSlotsLoading: boolean;
  init: () => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Resume the stored match. */
  acceptResume: () => void;
  /** Ignore the cloud save and start a fresh match. */
  declineResume: () => void;
  /** Explicit write (round end, turn pass, manual). */
  saveNow: () => Promise<void>;
  /** Named manual save slots (Save Manager). */
  refreshSaveSlots: () => Promise<void>;
  saveNamedSlot: (name: string) => Promise<void>;
  loadNamedSlot: (saveId: string) => Promise<void>;
  deleteNamedSlot: (saveId: string) => Promise<void>;
  /** Archive the live match into `users/{uid}/games` (used before a new game). */
  archiveCurrent: () => Promise<void>;
  dismissPrompt: () => void;
}

let unsubscribeGame: (() => void) | null = null;
let autosaveTimer: number | null = null;
let lastSignature = '';

const signatureOf = (state: ReturnType<typeof useGameStore.getState>): string =>
  `${state.epoch}:${state.market.currentRound}:${state.currentPlayerIndex}:${state.gameOver ? 1 : 0}`;

/** Firestore document shape: metadata + the full serialized match in `payload`. */
const snapshotDocument = (snapshot: CloudGameSnapshot) => ({
  schema: 'equity-tycoon/game',
  version: CLOUD_SNAPSHOT_VERSION,
  savedAt: snapshot.savedAt,
  round: snapshot.market.currentRound,
  phase: snapshot.market.phase,
  players: snapshot.players.filter((p) => !p.isBankrupt).length,
  payload: snapshot as unknown as Record<string, unknown>,
});


/** Wire auth subscription + the debounced autosave watcher (idempotent). */
const wireCloud = async (): Promise<void> => {
  const store = useCloudStore.getState();
  if (store.wired) return;
  let firebase: typeof import('../lib/firebase');
  try {
    firebase = await import('../lib/firebase');
  } catch (error) {
    useCloudStore.setState({ status: 'ERROR', error: String(error), wired: false });
    return;
  }
  if (!firebase.isCloudConfigured()) {
    useCloudStore.setState({ configured: false, status: 'OFF', wired: false });
    return;
  }
  useCloudStore.setState({ wired: true, configured: true, status: 'IDLE' });
  void firebase.initCloudAnalytics();

  firebase.subscribeAuth((user) => {
    useCloudStore.setState({ user });
    if (!user) {
      useCloudStore.setState({ pending: null, promptOpen: false, status: 'IDLE', lastSavedRound: null, lastSavedAt: null, saveSlots: [], saveSlotsLoading: false });
      return;
    }
    void firebase.syncCloudProfile(user);
    void (async () => {
      useCloudStore.setState({ status: 'LOADING', saveSlotsLoading: true });
      const document = await firebase.loadActiveGame(user.uid);
      const payload = document && !document.cleared ? (document.payload as unknown as CloudGameSnapshot) : null;
      try {
        const slots = await firebase.listNamedSlots(user.uid);
        useCloudStore.setState({ saveSlots: slots });
      } catch {
        useCloudStore.setState({ saveSlots: [] });
      }
      useCloudStore.setState({ saveSlotsLoading: false });
      if (payload && payload.gameStarted && !payload.gameOver) {
        useCloudStore.setState({ pending: payload, promptOpen: true, status: 'IDLE' });
      } else {
        useCloudStore.setState({ status: 'IDLE' });
      }
    })();
  });

  // Debounced autosave: fires on round completion, turn passes and match restarts.
  unsubscribeGame?.();
  unsubscribeGame = useGameStore.subscribe((state, previous) => {
    if (!useCloudStore.getState().user) return;
    if (!state.gameStarted) return;
    const signature = signatureOf(state);
    if (signature === lastSignature) return;
    lastSignature = signature;
    if (previous && state.gameOver && !previous.gameOver) void useCloudStore.getState().archiveCurrent();
    if (autosaveTimer !== null) window.clearTimeout(autosaveTimer);
    autosaveTimer = window.setTimeout(() => {
      autosaveTimer = null;
      void useCloudStore.getState().saveNow();
    }, CLOUD_AUTOSAVE_DEBOUNCE_MS);
  });
};

export const useCloudStore = create<CloudState>()((set, get) => ({
  configured: isConfigPresent(),
  initialized: false,
  wired: false,
  user: null,
  status: isConfigPresent() ? 'IDLE' : 'OFF',
  error: null,
  signingIn: false,
  lastSavedRound: null,
  lastSavedAt: null,
  pending: null,
  promptOpen: false,
  saveSlots: [],
  saveSlotsLoading: false,

  init: async () => {
    if (get().initialized) return;
    set({ initialized: true });
    // The SDK is only downloaded on boot when this browser already holds a Firebase
    // session — everyone else plays (and saves locally) without it.
    if (hasPersistedSession()) await wireCloud();
  },

  signIn: async () => {
    let firebase: typeof import('../lib/firebase');
    try {
      firebase = await import('../lib/firebase');
    } catch (error) {
      set({ status: 'ERROR', error: String(error) });
      return;
    }
    set({ signingIn: true, error: null });
    try {
      const user = await firebase.signInWithGoogle();
      set({ user, signingIn: false, status: 'IDLE', configured: true });
      await wireCloud();
    } catch (error) {
      set({ signingIn: false, error: String(error), status: 'ERROR' });
    }
  },

  signOut: async () => {
    const firebase = await import('../lib/firebase');
    await firebase.signOutFromGoogle();
    set({ user: null, pending: null, promptOpen: false, status: 'IDLE', lastSavedAt: null, lastSavedRound: null });
  },

  acceptResume: () => {
    const pending = get().pending;
    if (!pending) return;
    useGameStore.getState().hydrateFromCloud(pending);
    set({ pending: null, promptOpen: false, status: 'SAVED', lastSavedRound: pending.market.currentRound, lastSavedAt: pending.savedAt });
  },

  declineResume: () => {
    const user = get().user;
    set({ pending: null, promptOpen: false });
    // Fresh match: back to the setup lobby, and stop offering the old cloud save.
    useGameStore.getState().openLobby();
    if (user) void import('../lib/firebase').then((firebase) => firebase.clearActiveGame(user.uid));
  },

  dismissPrompt: () => set({ promptOpen: false }),

  saveNow: async () => {
    const user = get().user;
    if (!user) return;
    const firebase = await import('../lib/firebase');
    const snapshot = useGameStore.getState().snapshotForCloud();
    set({ status: 'SAVING', error: null });
    try {
      await firebase.saveActiveGame(user.uid, snapshotDocument(snapshot));
      set({ status: 'SAVED', lastSavedAt: snapshot.savedAt, lastSavedRound: snapshot.market.currentRound });
    } catch (error) {
      set({ status: 'ERROR', error: String(error) });
    }
  },

  refreshSaveSlots: async () => {
    const user = get().user;
    if (!user) {
      set({ saveSlots: [], saveSlotsLoading: false });
      return;
    }
    set({ saveSlotsLoading: true });
    try {
      const firebase = await import('../lib/firebase');
      const slots = await firebase.listNamedSlots(user.uid);
      set({ saveSlots: slots, saveSlotsLoading: false });
    } catch (error) {
      set({ saveSlotsLoading: false, error: String(error) });
    }
  },

  saveNamedSlot: async (name: string) => {
    const user = get().user;
    if (!user) throw new Error('cloud-not-configured');
    const firebase = await import('../lib/firebase');
    const trimmed = name.trim();
    const state = useGameStore.getState();
    const snapshot = snapshotDocument(state.snapshotForCloud());
    // Sanitized slot id: no spaces, slashes or empty strings can break the doc path.
    const saveId = `slot-${safeSlotId(trimmed)}-${Date.now()}`;
    set({ status: 'SAVING', error: null });
    try {
      await firebase.saveNamedSlot(user.uid, saveId, trimmed || `Kolo ${snapshot.round} · ${snapshot.players} hráčů`, snapshot);
      set({ status: 'SAVED', lastSavedAt: snapshot.savedAt, lastSavedRound: snapshot.round });
      await get().refreshSaveSlots();
    } catch (error) {
      set({ status: 'ERROR', error: String(error) });
      throw error;
    }
  },

  loadNamedSlot: async (saveId: string) => {
    const user = get().user;
    if (!user) return;
    const firebase = await import('../lib/firebase');
    set({ status: 'LOADING', error: null });
    try {
      const slot = await firebase.loadNamedSlot(user.uid, saveId);
      if (!slot) {
        set({ status: 'ERROR', error: 'save-not-found' });
        return;
      }
      const payload = slot.gameState as unknown as CloudGameSnapshot;
      useGameStore.getState().hydrateFromCloud(payload);
      set({ status: 'SAVED', pending: null, promptOpen: false, lastSavedAt: Date.now(), lastSavedRound: slot.summary.round });
    } catch (error) {
      set({ status: 'ERROR', error: String(error) });
    }
  },

  deleteNamedSlot: async (saveId: string) => {
    const user = get().user;
    if (!user) return;
    const firebase = await import('../lib/firebase');
    await firebase.deleteNamedSlot(user.uid, saveId);
    await get().refreshSaveSlots();
  },

  archiveCurrent: async () => {
    const user = get().user;
    if (!user) return;
    const state = useGameStore.getState();
    if (!state.gameStarted) return;
    const firebase = await import('../lib/firebase');
    await firebase.pushCloudHistory(user.uid, snapshotDocument(state.snapshotForCloud()));
    state.logEvent(t('cloud.archived'), 'INFO');
  },
}));

export default useCloudStore;
